import React from 'react';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Clock,
  PlayCircle,
  PauseCircle,
  RefreshCw,
  AlertCircle,
  Loader
} from 'lucide-react';

const StatusBadge = ({ 
  status, 
  size = 'default', 
  showIcon = true, 
  pulse = false,
  onClick = null,
  className = '',
  type = 'task' // 'task' or 'project'
}) => {
  // Status configurations
  const statusConfig = {
    // Task statuses - more specific names to avoid confusion
    'ממתין לעבודה': {
      color: 'gray',
      icon: Clock,
      label: type === 'project' ? 'פרויקט חדש- ממתין לעבודה' : 'משימה חדשה- ממתינה לעבודה'
    },
    'במסלול': {
      color: 'blue',
      icon: PlayCircle,
      label: type === 'project' ? 'פרויקט בתהליך' : 'משימה בתהליך'
    },
    'הושלם': {
      color: 'green',
      icon: CheckCircle,
      label: type === 'project' ? 'פרויקט הושלם' : 'משימה הושלמה'
    },
    'ממתין': {
      color: 'gray',
      icon: Clock,
      label: 'ממתין'
    },
    'בהשהיה': {
      color: 'purple',
      icon: PauseCircle,
      label: 'בהשהיה'
    },
    'בביצוע': {
      color: 'indigo',
      icon: RefreshCw,
      label: 'בביצוע',
      animate: true
    },
    'ממתין לאישור': {
      color: 'yellow',
      icon: Clock,
      label: 'ממתין לאישור מהעובד',
      pulse: true
    },
    
    // Project statuses - renamed to avoid conflicts
    'פרויקט ממתין לעבודה': {
      color: 'slate',
      icon: Clock,
      label: 'פרויקט חדש- ממתין לעבודה'
    },
    'פרויקט בתהליך': {
      color: 'blue',
      icon: PlayCircle,
      label: 'פרויקט בתהליך'
    },
    'פרויקט הושלם': {
      color: 'green',
      icon: CheckCircle,
      label: 'פרויקט הושלם'
    },
    
    // General statuses
    'חדש': {
      color: 'cyan',
      icon: AlertCircle,
      label: 'חדש'
    },
    'דחוף': {
      color: 'red',
      icon: AlertTriangle,
      label: 'דחוף',
      pulse: true
    },
    'בבדיקה': {
      color: 'orange',
      icon: Loader,
      label: 'בבדיקה',
      animate: true
    }
  };

  // Get configuration for status
  const config = statusConfig[status] || {
    color: 'gray',
    icon: Clock,
    label: status || 'לא ידוע'
  };

  const Icon = config.icon;
  const shouldPulse = pulse || config.pulse;
  const shouldAnimate = config.animate;

  // Size classes
  const sizeClasses = {
    xs: 'px-2 py-0.5 text-xs',
    sm: 'px-2.5 py-1 text-xs',
    default: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  // Icon sizes
  const iconSizes = {
    xs: 12,
    sm: 14,
    default: 16,
    lg: 20
  };

  // Color classes
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800 ring-blue-600/20',
    green: 'bg-green-100 text-green-800 ring-green-600/20',
    yellow: 'bg-yellow-100 text-yellow-800 ring-yellow-600/20',
    red: 'bg-red-100 text-red-800 ring-red-600/20',
    purple: 'bg-purple-100 text-purple-800 ring-purple-600/20',
    gray: 'bg-gray-100 text-gray-800 ring-gray-600/20',
    indigo: 'bg-indigo-100 text-indigo-800 ring-indigo-600/20',
    slate: 'bg-slate-100 text-slate-800 ring-slate-600/20',
    cyan: 'bg-cyan-100 text-cyan-800 ring-cyan-600/20',
    orange: 'bg-orange-100 text-orange-800 ring-orange-600/20'
  };

  // Build classes
  const baseClasses = `
    inline-flex items-center gap-1.5 font-medium rounded-full ring-1 ring-inset
    ${sizeClasses[size]}
    ${colorClasses[config.color]}
    ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
    ${shouldPulse ? 'animate-pulse' : ''}
    ${className}
  `;

  return (
    <span 
      className={baseClasses}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      {showIcon && Icon && (
        <Icon 
          size={iconSizes[size]} 
          className={shouldAnimate ? 'animate-spin' : ''}
        />
      )}
      <span>{config.label}</span>
    </span>
  );
};

// Status group component for showing multiple statuses
export const StatusGroup = ({ statuses = [], size = 'sm', className = '' }) => {
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {statuses.map((status, index) => (
        <StatusBadge 
          key={`${status}-${index}`} 
          status={status} 
          size={size}
        />
      ))}
    </div>
  );
};

// Priority badge component
export const PriorityBadge = ({ priority, size = 'default', showIcon = true }) => {
  // New priority matrix
  const priorityConfig = {
    'important_urgent': {
      color: 'red',
      icon: AlertTriangle,
      label: 'חשוב ודחוף',
      pulse: true
    },
    'important_not_urgent': {
      color: 'orange',
      icon: AlertCircle,
      label: 'חשוב ולא דחוף'
    },
    'not_important_urgent': {
      color: 'blue',
      icon: Clock,
      label: 'לא חשוב ודחוף'
    },
    'not_important_not_urgent': {
      color: 'gray',
      icon: Clock,
      label: 'לא חשוב ולא דחוף'
    }
  };

  const config = priorityConfig[priority] || priorityConfig.important_not_urgent;
  const Icon = config.icon;

  const sizeClasses = {
    xs: 'px-2 py-0.5 text-xs',
    sm: 'px-2.5 py-1 text-xs',
    default: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  const iconSizes = {
    xs: 12,
    sm: 14,
    default: 16,
    lg: 20
  };

  const colorClasses = {
    red: 'bg-red-100 text-red-800 border-red-200',
    orange: 'bg-orange-100 text-orange-800 border-orange-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
    gray: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  return (
    <span className={`
      inline-flex items-center gap-1.5 font-medium rounded-md border
      ${sizeClasses[size]}
      ${colorClasses[config.color]}
      ${config.pulse ? 'animate-pulse' : ''}
    `}>
      {showIcon && <Icon size={iconSizes[size]} />}
      <span>{config.label}</span>
    </span>
  );
};

// Acknowledgment status badge
export const AcknowledgmentBadge = ({ acknowledged, size = 'default' }) => {
  if (acknowledged) {
    return (
      <StatusBadge 
        status="הושלם" 
        size={size}
        showIcon={true}
        className="!bg-green-50 !text-green-700 !ring-green-200"
      />
    );
  }

  return (
    <span className={`
      inline-flex items-center gap-1.5 font-medium rounded-full
      px-3 py-1 text-sm
      bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-200
      animate-pulse
    `}>
      <Clock size={16} />
      <span>ממתין לאישור</span>
    </span>
  );
};

// Custom status badge for dynamic statuses
export const CustomStatusBadge = ({ 
  label, 
  color = 'gray', 
  icon: CustomIcon, 
  size = 'default',
  className = '' 
}) => {
  const sizeClasses = {
    xs: 'px-2 py-0.5 text-xs',
    sm: 'px-2.5 py-1 text-xs',
    default: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  const iconSizes = {
    xs: 12,
    sm: 14,
    default: 16,
    lg: 20
  };

  return (
    <span className={`
      inline-flex items-center gap-2 font-medium rounded-full
      ${sizeClasses[size]}
      ${className}
    `}
    style={{
      backgroundColor: `${color}20`,
      color: color,
      border: `1px solid ${color}40`
    }}>
      {CustomIcon && <CustomIcon size={iconSizes[size]} />}
      <span>{label}</span>
    </span>
  );
};

// Usage examples:
/*
// Basic usage
<StatusBadge status="במסלול" />

// Different sizes
<StatusBadge status="בסיכון" size="xs" />
<StatusBadge status="חסום" size="lg" />

// Without icon
<StatusBadge status="הושלם" showIcon={false} />

// With click handler
<StatusBadge 
  status="ממתין" 
  onClick={() => console.log('clicked')}
/>

// Priority badge
<PriorityBadge priority="urgent" />

// Acknowledgment badge
<AcknowledgmentBadge acknowledged={false} />

// Status group
<StatusGroup statuses={['במסלול', 'בסיכון', 'חסום']} />

// Custom status
<CustomStatusBadge 
  label="מותאם אישית" 
  color="#8B5CF6" 
  icon={Star}
/>
*/

export default StatusBadge;